import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../api/client";
import type { SharedManuscriptInfo } from "../api/types";
import { Icon } from "../components/icons";

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/**
 * SharedManuscript — the public, no-login manuscript download page (route
 * `/share/manuscript/:token`), so proof listeners get their own copy of the PDF
 * straight from Mockingbird. Its own link, separate from the Pickups Package.
 */
export function SharedManuscript() {
  const { token } = useParams();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["share-manuscript", token],
    queryFn: () => api<SharedManuscriptInfo>(`/api/manuscript-share/${token}`),
    enabled: !!token,
    retry: false,
  });

  const meta = data
    ? [data.pages ? `${data.pages} page${data.pages === 1 ? "" : "s"}` : null, "PDF", formatSize(data.size_bytes)]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <div className="pickups-standalone">
      <header className="pk-topbar">
        <a className="pk-brand" href="/" target="_blank" rel="noopener noreferrer" title="About Mockingbird">
          <Icon name="logo" size={22} />
          <span className="pk-brand-name">Mockingbird</span>
        </a>
      </header>

      <div className="page sm-page">
        {isError ? (
          <div className="empty-state">
            <h3>This link isn’t available</h3>
            <p>The download link may have been turned off, or the address is incorrect.</p>
          </div>
        ) : isLoading || !data ? (
          <div className="empty-state"><p>Loading…</p></div>
        ) : (
          <div className="sm-card">
            <div className="pk-eyebrow">Manuscript</div>
            <h1 className="pk-title">{data.project_name}</h1>
            <div className="sm-file">
              <span className="sm-file-icon"><Icon name="file-text" size={22} /></span>
              <span className="sm-file-text">
                <span className="sm-file-name">{data.filename}</span>
                <span className="sm-file-meta">{meta}</span>
              </span>
            </div>

            <a className="btn-share btn-share-primary sm-download" href={`/api/manuscript-share/${token}/file`} download={data.filename}>
              <Icon name="download" size={16} /> Download PDF
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
